import { faMessage, faUser } from '@fortawesome/pro-solid-svg-icons';
import { useEffect, useMemo } from 'react';

import { useTranslate } from '@growtherapy/i18n';
import { Button, Icon, Tag, cn } from '@growtherapy/sprout-ui';

import { DrawerButton } from '../../components/DrawerButton';
import { useActiveBrand } from '../../components/branding/useActiveBrand';
import { secondaryInverseSessionControlButtonClassName } from '../../components/secondaryInverseSessionControlButtonClassName';
import { setAlertTitle, setDefaultFaviconAndTitle } from '../../metadata';
import { DRAWER_STATES_CONTROLLED_BY_CLIENT_INFO_BUTTON } from '../client-information/constants';
import { PUSH_DRAWER_ARIA_CONTROLS_ID } from '../constants';
import { ProviderDrawerState } from '../state';
import { getIsClientListDrawerOpen } from '../utils';

export function ClientsDrawerButton({
  drawerState,
  numberUnreadMessages,
  numberWaiting,
  onClick,
}: {
  drawerState: ProviderDrawerState | null;
  numberUnreadMessages: number;
  numberWaiting: number;
  onClick: () => void;
}) {
  const t = useTranslate();
  const isClientsDrawerOpen = getIsClientListDrawerOpen(drawerState);
  const ariaLabel = isClientsDrawerOpen ? 'Close clients drawer' : 'Open clients drawer';
  const activeBrand = useActiveBrand();

  useEffect(
    function showClientNotificationInFavIconAndTitle() {
      if (!numberWaiting) return setDefaultFaviconAndTitle(t, activeBrand);
      const clientNotificationTitle = `(${numberWaiting}) client${numberWaiting > 1 ? 's' : ''} waiting`;
      setAlertTitle(clientNotificationTitle, activeBrand);
    },
    [t, numberWaiting, activeBrand],
  );

  const screenreaderLabel = useMemo(() => {
    const waitingClients = numberWaiting ? `, ${numberWaiting} client${numberWaiting > 1 ? 's' : ''} waiting` : '';
    const unreadMessages = numberUnreadMessages
      ? `, ${numberUnreadMessages} unread message${numberUnreadMessages > 1 ? 's' : ''}`
      : '';
    return `${ariaLabel}${waitingClients}${unreadMessages}`;
  }, [ariaLabel, numberUnreadMessages, numberWaiting]);

  return (
    <DrawerButton
      as={Button}
      // NOTE: This list overlaps with the drawer states controlled by the
      // `ClientInformationButton`. This is intentional and ensures that when
      // that button is not available because a provider is not in session, this
      // button can still be focused on drawer close instead.
      forDrawerStates={[ProviderDrawerState.CLIENTS, ...DRAWER_STATES_CONTROLLED_BY_CLIENT_INFO_BUTTON]}
      aria-label={ariaLabel}
      aria-expanded={isClientsDrawerOpen}
      aria-controls={PUSH_DRAWER_ARIA_CONTROLS_ID}
      data-testid='clients-drawer-button'
      use='secondary'
      className={cn('hidden md:flex items-center gap-x-2', secondaryInverseSessionControlButtonClassName)}
      onClick={onClick}
    >
      <Icon aria-hidden icon={faUser} className='mr-2' />
      <span className='sr-only'>{screenreaderLabel}</span>
      <span aria-hidden>Clients</span>
      {!!numberWaiting && (
        <Tag aria-hidden className='bg-lilac-100 lowercase'>
          {numberWaiting} waiting
        </Tag>
      )}
      {!!numberUnreadMessages && (
        <Tag aria-hidden className='bg-lilac-100 lowercase'>
          <Icon icon={faMessage} className='mr-1' /> {numberUnreadMessages}
        </Tag>
      )}
    </DrawerButton>
  );
}
